/**
 * De qué canal viene una venta, leyendo las referencias del documento.
 *
 * POR QUÉ SE CALCULA ACÁ
 *
 * El canal tiene dos fuentes y ninguna sirve sola:
 *
 *   - `shopify_orders.canal`, que es el dato bueno, pero existe solo si el
 *     pedido ya se sincronizó y se cruzó. Llega en la info de pago.
 *   - Las referencias del documento, que están en TODOS los documentos desde
 *     que se emitieron, incluidos los de antes de que existiera la
 *     integración.
 *
 * Así la columna muestra el canal del primer día, sin esperar ninguna
 * sincronización. Cuando el cruce existe, gana el canal de la base.
 *
 * ESPEJO DEL BACKEND: la misma regla vive en
 * `src/services/v1/common/metodoPagoCanal.js` del repo del backend, que es
 * la que decide qué pasarela se imprime en el PDF. Son repos separados y no
 * se puede compartir el módulo; si se agrega un canal, va en los dos.
 */

/** 801 = orden de compra, que es la referencia al pedido del canal. */
const TPO_DOC_REF_PEDIDO = 801;

/**
 * Palabras que delatan el canal. Se comparan sin espacios ni acentos, así
 * "Mercado Libre", "MercadoLibre" y "mercado  libre" son lo mismo.
 */
const PALABRAS_DE_CANAL = [
  ["shopify", "shopify"],
  ["mercadolibre", "mercadolibre"],
  ["meli", "mercadolibre"],
  ["falabella", "falabella"],
];

/** Cómo se llama cada canal cuando se le muestra a una persona. */
export const NOMBRE_DE_CANAL = {
  shopify: "Shopify",
  mercadolibre: "MercadoLibre",
  falabella: "Falabella",
};

/**
 * Por dónde cobra cada canal.
 *
 * Es el piso del método de pago, para los documentos emitidos ANTES de que
 * la pasarela se guardara en `notes` y que todavía no tengan pedido ni pago
 * cruzado. Sin esto la celda decía "Sin método de pago" en ventas que sí se
 * sabe cómo se cobraron.
 *
 * NO ES UNA SUPOSICIÓN: es la misma constante con la que el backend decide
 * qué pasarela imprimir en el PDF (`METODO_FIJO_POR_CANAL`). La tienda cobra
 * únicamente por Mercado Pago y Falabella por Fpay. Si eso cambia, cambia en
 * los dos lados.
 *
 * Lo que llega del pedido o de Mercado Pago siempre gana, porque dice el
 * medio real ("Tarjeta de crédito · Visa") y no solo la pasarela.
 */
export const PASARELA_DE_CANAL = {
  shopify: "Mercado Pago (Shopify)",
  mercadolibre: "Mercado Pago",
  falabella: "Fpay",
};

function aplanar(valor) {
  return String(valor ?? "")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
}

function esReferenciaDePedido(linea) {
  const tipo = linea?.TpoDocRef ?? linea?.tpoDocRef ?? linea?.docType ?? linea?.code;
  return Number(tipo) === TPO_DOC_REF_PEDIDO;
}

/**
 * @param {Array} referencias Las del documento (`dte_documents.referencias`).
 * @returns {string|null} shopify | mercadolibre | falabella
 */
export function detectarCanalEnReferencias(referencias) {
  if (!Array.isArray(referencias)) return null;

  for (const linea of referencias) {
    if (!linea || typeof linea !== "object") continue;
    // Solo la referencia al pedido: una nota de crédito apunta a su factura
    // con otro tipo, y mirarlas todas podía tomar el canal equivocado.
    if (!esReferenciaDePedido(linea)) continue;

    // Las dos formas de la misma referencia: los nombres del XML al emitir
    // (RazonRef, FolioRef) y los normalizados que quedan en la base
    // (reason, folio).
    const texto = aplanar([
      linea.RazonRef ?? linea.razonRef ?? linea.reason,
      linea.FolioRef ?? linea.folioRef ?? linea.folio,
      linea.GlosaRef ?? linea.glosaRef ?? linea.glosa,
    ].filter(Boolean).join(" "));
    if (!texto) continue;

    for (const [palabra, canal] of PALABRAS_DE_CANAL) {
      if (texto.includes(palabra)) return canal;
    }
  }

  return null;
}

/**
 * Canal de venta de un documento.
 *
 * Devuelve el canal solo si es uno conocido: un canal que el frontend no
 * sabe nombrar no se muestra con un nombre inventado.
 *
 * @param {object} documento Fila del listado de documentos emitidos.
 * @param {object|null} infoPago Info de pago de ese documento, si la hay.
 * @returns {string|null} "shopify" | "mercadolibre" | "falabella" | null
 */
export function canalDeVenta(documento, infoPago = null) {
  // Primero el de la base, que es un dato declarado y no deducido.
  const deLaBase = String(infoPago?.canal || "").trim().toLowerCase();
  if (deLaBase) return NOMBRE_DE_CANAL[deLaBase] ? deLaBase : null;

  const detectado = detectarCanalEnReferencias(documento?.referencias);
  return detectado && NOMBRE_DE_CANAL[detectado] ? detectado : null;
}

/**
 * ¿Hay que nombrar la pasarela arriba, junto al canal?
 *
 * Solo cuando el renglón de abajo no la nombra ya. Si el medio de pago es
 * "Mercado Pago (Shopify)", repetir "Mercado Pago" arriba no agrega nada;
 * si es "Transferencia bancaria · Fintoc", sin la pasarela arriba la fila
 * parece una transferencia directa a la cuenta de la empresa, que es otra
 * plata y se concilia en otro lado.
 *
 * @param {string|null} pasarela Por dónde entró la plata ("Mercado Pago").
 * @param {string|null} etiqueta Lo que se muestra abajo como medio de pago.
 * @returns {string|null} La pasarela, o null si abajo ya se dice.
 */
export function pasarelaAparte(pasarela, etiqueta) {
  const texto = String(pasarela ?? "").trim();
  if (!texto) return null;
  const plana = aplanar(texto);
  const abajo = aplanar(String(etiqueta ?? ""));
  if (plana && abajo && (abajo.includes(plana) || plana.includes(abajo))) return null;
  return texto;
}
